/* src/components/admin/announcements/AdminAnnouncementsLogic.js */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";

// ==========================================
// INTERNAL HELPERS
// ==========================================
//function toDateStart(dateStr) {
//  const d = new Date(`${dateStr}T00:00:00`);
//  return d;
//}

async function getAdminProfile() {
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in.");

  const snap = await getDoc(doc(db, "users", user.uid));
  const data = snap.exists() ? snap.data() : {};

  return {
    uid: user.uid,
    email: user.email || data.email || "",
    name: data.name || user.displayName || "Admin",
    role: data.role || "unknown",
  };
}

function stripHtmlToText(html) {
  if (!html) return "";
  return String(html)
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export async function toggleArchive(announcementId, isArchived) {
  // If isArchived is true, we are restoring it (setting to false)
  // If isArchived is false/undefined, we are archiving it (setting to true)
  const newState = !isArchived; 
  await updateAnnouncement(announcementId, { 
    archived: newState,
    pinned: false // Optional: Unpin items when archiving so they don't get stuck in the sidebar
  });
}

// ==========================================
// 1. ANNOUNCEMENTS CRUD
// ==========================================
export function subscribeAnnouncements(onRows) {
  const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => {
      const data = d.data() || {};
      const createdAtMs =
        data.createdAt && typeof data.createdAt.toMillis === "function"
          ? data.createdAt.toMillis()
          : null;

      return {
        id: d.id,
        ...data,
        createdAtMs,
      };
    });
    onRows(rows);
  });
}

export async function createAnnouncement(payload) {
  const admin = await getAdminProfile();
  // REMOVED: Date parsing logic for start/end dates
  const now = Date.now();

  const category = (payload.category || "").trim() || "General";

  const docData = {
    title: (payload.title || "").trim(),
    contentHtml: payload.contentHtml || "",
    contentText: stripHtmlToText(payload.contentHtml || ""),
    category,

    audienceType: payload.audienceType,
    audienceUserIds: payload.audienceType === "users" ? (payload.audienceUserIds || []) : [],
    
    // REMOVED: startAt, endAt, alwaysShowStartDate
    
    commentsEnabled: payload.commentsEnabled !== false,
    notifyTutors: !!payload.notifyTutors,

    pinned: false,
    orderIndex: now,
    createdBy: admin.uid,
    createdByName: admin.name,
    createdByEmail: admin.email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (!docData.title) throw new Error("Title is required.");
  if (!docData.contentHtml) throw new Error("Content is required.");
  if (docData.audienceType === "users" && docData.audienceUserIds.length === 0) {
    throw new Error("Please select at least one employee.");
  }

  const docRef = await addDoc(collection(db, "announcements"), docData);
  return docRef.id;
}

export async function updateAnnouncement(announcementId, updates) {
  const patch = { ...(updates || {}), updatedAt: serverTimestamp() };
  
  if (typeof patch.category === "string") {
    patch.category = patch.category.trim() || "General";
  }
  if (typeof patch.contentHtml === "string") {
    patch.contentText = stripHtmlToText(patch.contentHtml);
  }

  // --- ADD THIS BLOCK ---
  // If the user checked "Send notification", add a timestamp to force the update
  // to register as a "change" in Firestore, ensuring the backend triggers.
  if (patch.notifyTutors === true) {
    patch.notificationTriggeredAt = serverTimestamp();
  }
  // ----------------------

  const ref = doc(db, "announcements", announcementId);
  await updateDoc(ref, patch);
}

export async function deleteAnnouncement(announcementId) {
  const ref = doc(db, "announcements", announcementId);
  await deleteDoc(ref);
}

export async function setPinned(announcementId, pinned) {
  const ref = doc(db, "announcements", announcementId);
  await updateDoc(ref, { pinned: !!pinned, updatedAt: serverTimestamp() });
}

export async function swapOrderIndex(aId, aOrderIndex, bId, bOrderIndex) {
  const aRef = doc(db, "announcements", aId);
  const bRef = doc(db, "announcements", bId);
  await updateDoc(aRef, { orderIndex: bOrderIndex, updatedAt: serverTimestamp() });
  await updateDoc(bRef, { orderIndex: aOrderIndex, updatedAt: serverTimestamp() });
}

// ==========================================
// 2. AUDIENCE / TUTORS
// ==========================================
export function subscribeActiveTutorCount(onCount) {
  const q = query(collection(db, "users"), where("role", "==", "tutor"));
  return onSnapshot(
    q,
    (snap) => {
      let count = 0;
      snap.docs.forEach((d) => {
        const u = d.data() || {};
        const st = (u.status || "active").toLowerCase();
        if (st === "active") count += 1;
      });
      onCount(count);
    },
    () => onCount(0)
  );
}

// ==========================================
// 3. READ RECEIPTS
// ==========================================
export function subscribeReadsCount(announcementId, onCount) {
  const ref = collection(db, "announcements", announcementId, "reads");
  return onSnapshot(ref, (snap) => onCount(snap.size));
}

export function subscribeReadsList(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "reads"),
    orderBy("readAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => {
        const data = d.data() || {};
        const readAtMs =
          data.readAt && typeof data.readAt.toMillis === "function"
            ? data.readAt.toMillis()
            : null;
        return { id: d.id, ...data, readAtMs };
      });
      onRows(rows);
    },
    () => onRows([])
  );
}

export async function markAnnouncementRead(announcementId) {
  const me = await getAdminProfile();
  const ref = doc(db, "announcements", announcementId, "reads", me.uid);
  await setDoc(
    ref,
    {
      uid: me.uid,
      userName: me.name,
      userEmail: me.email,
      userRole: me.role,
      readAt: serverTimestamp(),
    },
    { merge: true }
  );
}

// ==========================================
// 4. COMMENTS
// ==========================================
export function subscribeComments(announcementId, onRows) {
  const q = query(
    collection(db, "announcements", announcementId, "comments"),
    orderBy("createdAt", "asc")
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onRows(rows);
    },
    (err) => console.error("subscribeComments error:", err)
  );
}

export async function addComment(announcementId, text, parentId = null) {
  const me = await getAdminProfile();
  let commentText = text;
  let commentParentId = parentId;

  if (typeof text === "object" && text !== null) {
    commentText = text.text;
    commentParentId = text.parentId ?? null;
  }

  const clean = (commentText || "").trim();
  if (!clean) throw new Error("Comment cannot be empty.");

  await addDoc(collection(db, "announcements", announcementId, "comments"), {
    uid: me.uid,
    userName: me.name,
    userEmail: me.email,
    userRole: me.role,
    text: clean,
    parentId: commentParentId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteComment(announcementId, commentId) {
  await deleteDoc(doc(db, "announcements", announcementId, "comments", commentId));
}


// ==========================================
// 5. CATEGORY MANAGEMENT
// ==========================================

export function subscribeCategories(onRows) {
  const q = query(collection(db, "announcement_categories"), orderBy("name", "asc"));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onRows(rows);
  });
}

export async function addCategory(name) {
  const clean = (name || "").trim();
  if (!clean) throw new Error("Name required");
  await addDoc(collection(db, "announcement_categories"), { 
    name: clean,
    createdAt: serverTimestamp() 
  });
}

export async function deleteCategory(id) {
  await deleteDoc(doc(db, "announcement_categories", id));
}

export async function renameCategory(catId, oldName, newName) {
  const clean = (newName || "").trim();
  if (!clean) throw new Error("Name required");

  const batch = writeBatch(db);

  // 1. Update the category definition
  const catRef = doc(db, "announcement_categories", catId);
  batch.update(catRef, { name: clean });

  // 2. Find all announcements with the old category name and update them
  const q = query(collection(db, "announcements"), where("category", "==", oldName));
  
  // FIX: used top-level getDocs, removed the unused 'snap' variable
  const postsSnap = await getDocs(q);

  postsSnap.forEach((docSnap) => {
    batch.update(docSnap.ref, { category: clean });
  });

  await batch.commit();
}